

import React, { useState, useEffect, useRef, useCallback } from "react";
import birthdayMusic from "../../assets/audio/birthday-voice.mp3";
import anniversaryMusic from "../../assets/audio/anniversary-instrumental.mp3";

const COLORS = [
  "#ff6b6b", "#ffd93d", "#6bcb77", "#4d96ff", "#c77dff", "#ff9f43", "#ff6bcd",
]

function rand(a, b) {
  return a + Math.random() * (b - a)
}

function getOrdinal(n) {
  if (n % 100 >= 11 && n % 100 <= 13) return `${n}th`
  switch (n % 10) {
    case 1: return `${n}st`
    case 2: return `${n}nd`
    case 3: return `${n}rd`
    default: return `${n}th`
  }
}

function safeGetItem(key) {
  try { return localStorage.getItem(key) } catch (e) { return null }
}

function safeRemoveItem(key) {
  try { localStorage.removeItem(key) } catch (e) { /* ignore */ }
}

const MUSIC = {
  birthday: birthdayMusic,
  anniversary: anniversaryMusic,
}


const numberToWord = (num) => {
  const ones = [
    "zero", "One", "Two", "Three", "Four",
    "Five", "Six", "Seven", "Eight", "Nine",
    "Ten", "Eleven", "Twelve", "Thirteen",
    "Fourteen", "Fifteen", "Sixteen", "Seventeen",
    "Eighteen", "Nineteen"
  ];

  const tens = [
    "", "", "Twenty", "Thirty", "Forty",
    "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"
  ];

  if (num < 20) return ones[num];
  if (num < 100) {
    return tens[Math.floor(num / 10)] +
      (num % 10 ? `-${ones[num % 10]}` : "");
  }

  return num.toString();
};



const CONTENT = {
  birthday: {
    emoji: "🎁✨",

    getTitle: (name, age) =>
      `🎂🎉 Happy Birthday, ${name}! 🎉🎂`,

    getMessage: age => (
      <>
        Wishing you a very happy birthday filled with lots of fun, laughter,
        happiness and joy! 🥳🍰
        <br />
        Have the most wonderful day and an amazing year ahead! 💕✨
      </>
    ),
  },

  anniversary: {
    emoji: "🏆✨",

    getTitle: (name, years) =>
      `🎉 Celebrating ${years ? `${numberToWord(years)} Year${years === 1 ? "" : "s"}` : "another year"} of Learning with Us! 🎉`,

    getMessage: years => (
      <>
        What a wonderful year it has been! From new lessons to new challenges,
        you’ve come such a long way. 🌟
        <br />
        We’re delighted to be part of your learning journey.
        <br />
        Keep going, {safeGetItem("username") || "there"}! 🎓🚀
      </>
    ),
  },
};

/**
 * AnniversaryOverlay
 *
 * Self-contained: reads show_birthday / show_anniversary / celebration_age /
 * celebration_years / username straight from localStorage - no props
 * needed. That's what Login.js and Authmiddleware.js both populate, so
 * mounting this ONE instance in Authmiddleware (which wraps every
 * authenticated route, unlike Layout.js which only covers one layout
 * variant) is enough to cover the whole app.
 *
 * Listens for a "celebration-check" window event so it can react to
 * checkUser's response landing *after* this component has already
 * mounted (page refresh case), not just at initial mount (fresh login
 * case).
 */
const AnniversaryOverlay = () => {
  const [queue, setQueue] = useState([])
  const [active, setActive] = useState(null)
  //const shownTypesRef = useRef(new Set()) // prevents showing the same type twice in one session
  const canvasRef = useRef(null)
  const ambientAnimRef = useRef(null)
  const audioRef = useRef(null)

  const lastShownKeyRef = useRef(null) // instead of a Set — just blocks back-to-back dupes in the same tick

const checkAndQueue = useCallback(() => {
  const sB = safeGetItem("show_birthday") === "1"
  const sA = safeGetItem("show_anniversary") === "1"
  const a = safeGetItem("celebration_age")
  const y = safeGetItem("celebration_years")

  const additions = []
  if (sB) additions.push({ type: "birthday", value: a ? Number(a) : null })
  if (sA) additions.push({ type: "anniversary", value: y ? Number(y) : null })
  if (additions.length > 0) {
    setQueue(prev => [...prev, ...additions])
  }

  safeRemoveItem("show_birthday")
  safeRemoveItem("show_anniversary")
  safeRemoveItem("celebration_age")
  safeRemoveItem("celebration_years")
}, [])
  // const checkAndQueue = useCallback(() => {
  //   const sB = safeGetItem("show_birthday") === "1"
  //   const sA = safeGetItem("show_anniversary") === "1"
  //   const a = safeGetItem("celebration_age")
  //   const y = safeGetItem("celebration_years")

  //   const additions = []

  //   if (sB && !shownTypesRef.current.has("birthday")) {
  //     shownTypesRef.current.add("birthday")
  //     additions.push({ type: "birthday", value: a ? Number(a) : null })
  //   }
  //   if (sA && !shownTypesRef.current.has("anniversary")) {
  //     shownTypesRef.current.add("anniversary")
  //     additions.push({ type: "anniversary", value: y ? Number(y) : null })
  //   }

  //   if (additions.length > 0) {
  //     setQueue(prev => [...prev, ...additions])
  //   }

  //   // Consume the flags so a later re-check (or refresh) doesn't replay them.
  //   safeRemoveItem("show_birthday")
  //   safeRemoveItem("show_anniversary")
  //   safeRemoveItem("celebration_age")
  //   safeRemoveItem("celebration_years")
  // }, [])

  // Check once on mount (covers the fresh-login flow, where localStorage
  // is already populated by the time this mounts)...
  useEffect(() => {
    checkAndQueue()
  }, [checkAndQueue])

  // ...and again whenever Authmiddleware's checkUser call resolves and
  // writes fresh data (covers the page-refresh flow, where that data
  // lands *after* this component already mounted).
  useEffect(() => {
    window.addEventListener("celebration-check", checkAndQueue)
    return () => window.removeEventListener("celebration-check", checkAndQueue)
  }, [checkAndQueue])

  // Pull the next queued item into `active` once the current one clears.
  useEffect(() => {
    if (!active && queue.length > 0) {
      const [next, ...rest] = queue
      setActive(next)
      setQueue(rest)
    }
  }, [active, queue])

  const resizeCanvas = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    canvas.width = window.innerWidth
    canvas.height = window.innerHeight
  }, [])

  const launchAmbient = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    resizeCanvas()
    const ctx = canvas.getContext("2d")

    if (ambientAnimRef.current) {
      cancelAnimationFrame(ambientAnimRef.current)
    }

    const particles = Array.from({ length: 140 }, () => ({
      x: rand(0, canvas.width),
      y: rand(-canvas.height, 0),
      vx: rand(-1, 1),
      vy: rand(1.5, 3.5),
      color: COLORS[Math.floor(Math.random() * COLORS.length)],
      w: rand(6, 13),
      h: rand(4, 8),
      angle: rand(0, Math.PI * 2),
      spin: rand(-0.06, 0.06),
      isCircle: Math.random() > 0.5,
    }))

    const fadeZoneHeight = 100

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      const fadeStart = canvas.height - fadeZoneHeight

      particles.forEach(p => {
        p.x += p.vx
        p.y += p.vy
        p.angle += p.spin

        if (p.y > canvas.height + 20) {
          p.y = rand(-50, -5)
          p.x = rand(0, canvas.width)
        }

        let alpha = 1
        if (p.y > fadeStart) {
          alpha = Math.max(0, 1 - (p.y - fadeStart) / fadeZoneHeight)
        }

        ctx.save()
        ctx.globalAlpha = alpha
        ctx.translate(p.x, p.y)
        ctx.rotate(p.angle)
        ctx.fillStyle = p.color

        if (p.isCircle) {
          ctx.beginPath()
          ctx.ellipse(0, 0, p.w / 2, p.h / 2, 0, 0, Math.PI * 2)
          ctx.fill()
        } else {
          ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h)
        }

        ctx.restore()
      })

      ambientAnimRef.current = requestAnimationFrame(draw)
    }

    draw()
  }, [resizeCanvas])

  useEffect(() => {
    if (!active) return

    launchAmbient()
    window.addEventListener("resize", resizeCanvas)

    return () => {
      if (ambientAnimRef.current) {
        cancelAnimationFrame(ambientAnimRef.current)
      }
      window.removeEventListener("resize", resizeCanvas)
    }
  }, [active, launchAmbient, resizeCanvas])
  const [needsMusicTap, setNeedsMusicTap] = useState(false)
  useEffect(() => {
    let playOnInteraction = null;

    if (!active) return;
    const src = MUSIC[active.type];
    
    if (src) {
      const audio = new Audio(src);
      audio.loop = true;
      audio.volume = 0.5;
      audioRef.current = audio;

      // This function runs on the very first interaction after a refresh
      // playOnInteraction = () => {
      //   audio.play()
      //     .then(() => {
      //       // Success! Clean up the global event listeners
      //       window.removeEventListener('click', playOnInteraction);
      //       window.removeEventListener('keydown', playOnInteraction);
      //       window.removeEventListener('pointerdown', playOnInteraction);
      //       setNeedsMusicTap(false)
      //     })
      //     .catch((err) => console.log("Playback still restricted:", err));
      // };

      // Attempt to play immediately
      audio.play()
        .then(() => {
          console.log("Audio playing automatically!");
          setNeedsMusicTap(false);
        })
        .catch((err) => {
          console.warn("Autoplay blocked by page refresh. Waiting for any user interaction...", err);
          
          // Listen to the absolute first click, touch, or keypress on the refreshed page
          // window.addEventListener('click', playOnInteraction);
          // window.addEventListener('keydown', playOnInteraction);
          // window.addEventListener('pointerdown', playOnInteraction);
          setNeedsMusicTap(true);
        });
    }

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      if (playOnInteraction) {
        // window.removeEventListener('click', playOnInteraction);
        // window.removeEventListener('keydown', playOnInteraction);
        // window.removeEventListener('pointerdown', playOnInteraction);
        setNeedsMusicTap(false)
      }
    };
  }, [active]);

  const handlePlayMusic = () => {
    if (audioRef.current) {
      audioRef.current.play()
        .then(() => setNeedsMusicTap(false))
        .catch((err) => console.log("Playback still restricted:", err))
    }
  }
  const handleClose = () => {
    if (ambientAnimRef.current) {
      cancelAnimationFrame(ambientAnimRef.current)
    }
    const canvas = canvasRef.current
    if (canvas) {
      canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height)
    }
    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current.currentTime = 0
      audioRef.current = null
    }
    setActive(null)
    setNeedsMusicTap(false)
  }

  if (!active) return null

  const content = CONTENT[active.type] || CONTENT.birthday

  return (
    <div className="ann-overlay">
      <canvas ref={canvasRef} className="ann-canvas" />
      <button className="ann-close" onClick={handleClose} aria-label="Close">
        ✕
      </button>
      <div className="ann-card">
        <span className="ann-emoji">{content.emoji}</span>
        {/* <h2 className="ann-title">{content.getTitle(safeGetItem("username") || "there")}</h2> */}
        <h2 className="ann-title">
          {content.getTitle(safeGetItem("username") || "there", active.value)}
        </h2>
        <p className="ann-sub">{content.getMessage(active.value)}</p>
        {needsMusicTap && (
          <button className="ann-music-btn" onClick={handlePlayMusic}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M3 10v4h4l5 5V5L7 10H3zm13.5 2c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.26 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
          </svg>
          Play music
        </button>
      )}
      </div>
    </div>
  )
}

export default AnniversaryOverlay